import { useState, useEffect } from 'react';
import './OrganizeEventDialog.css';
import { eventsAPI, communityEventsAPI } from './services/api';
import { useTranslation } from 'react-i18next';
import { getHourCycle } from './utils/dateUtils';
import InviteFriendsDialog from './InviteFriendsDialog';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { enUS, he, bg } from 'date-fns/locale';

const DATE_FNS_LOCALE = { en: enUS, he, bg };

function OrganizeEventDialog({ isOpen, onClose, communityEvent, currentUser }) {
  const { t, i18n } = useTranslation();
  const [eventDateTime, setEventDateTime] = useState(null);
  const [description, setDescription] = useState('');
  const [selectedFriends, setSelectedFriends] = useState([]);
  const [showFriendDialog, setShowFriendDialog] = useState(false);
  const [creating, setCreating] = useState(false);

  const is24h = getHourCycle(i18n.language) === 'h23';
  const dateFormat = is24h ? 'EEEE, d MMMM yyyy HH:mm' : 'EEEE, MMMM d, yyyy h:mm aa';
  const timeFormat = is24h ? 'HH:mm' : 'h:mm aa';

  useEffect(() => {
    if (isOpen) {
      setEventDateTime(null);
      setDescription('');
      setSelectedFriends([]);
    }
  }, [isOpen]);

  const handleFriendToggle = (userId) => {
    setSelectedFriends(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleCreateGathering = async () => {
    if (!eventDateTime) {
      alert(t('createGathering.alerts.selectDateTime'));
      return;
    }


    try {
      setCreating(true);

      // Check if gatherings already exist for this community event
      const existingGatherings = await communityEventsAPI.getGatherings(communityEvent.id);
      if (existingGatherings && existingGatherings.length > 0) {
        const confirmed = window.confirm(
          t('createGathering.confirms.gatheringExists', { count: existingGatherings.length })
        );
        if (!confirmed) {
          setCreating(false);
          return;
        }
      }

      const pad = (n) => String(n).padStart(2, '0');
      const dateStr = `${eventDateTime.getUTCFullYear()}-${pad(eventDateTime.getUTCMonth() + 1)}-${pad(eventDateTime.getUTCDate())}`;
      const timeStr = `${pad(eventDateTime.getUTCHours())}:${pad(eventDateTime.getUTCMinutes())}`;

      // Create a personal gathering based on the community event
      const eventData = {
        name: communityEvent.name,
        description: description,
        address: communityEvent.address,
        latitude: communityEvent.latitude,
        longitude: communityEvent.longitude,
        date: dateStr,
        time: timeStr,
        eventType: 5, // PersonalGathering
        hashtags: [], // No hashtags for personal gatherings
        friendIds: selectedFriends,
        suitableForPeople: communityEvent.suitableForPeople,
        communityEventId: communityEvent.id // Link to parent community event
      };

      await eventsAPI.createWithPhoto(eventData);
      alert(t('createGathering.alerts.created'));
      onClose(true); // Pass true to indicate gathering was created
    } catch (error) {
      console.error('Error creating gathering:', error);
      alert(t('createGathering.alerts.failed'));
    } finally {
      setCreating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog-content" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h2>{t('createGathering.title')}</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="dialog-body">
          <div className="form-section">
            <label>{t('createGathering.eventName')}</label>
            <input
              type="text"
              value={communityEvent?.name || ''}
              disabled
              className="disabled-input"
            />
          </div>

          <div className="form-section">
            <label>{t('createGathering.address')}</label>
            <input
              type="text"
              value={communityEvent?.address || ''}
              disabled
              className="disabled-input"
            />
          </div>

          {communityEvent?.suitableForPeople && (
            <div className="form-section">
              <label>{t('createGathering.suitableFor')}</label>
              <input
                type="text"
                value={t('createGathering.people', { count: communityEvent.suitableForPeople })}
                disabled
                className="disabled-input"
              />
            </div>
          )}

          <div className="form-section">
            <label>{t('createGathering.description')}</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('createGathering.descriptionPlaceholder')}
              rows="3"
              className="description-textarea"
            />
          </div>

          <div className="form-section">
            <label>{t('createGathering.dateTime')}</label>
            <DatePicker
              selected={eventDateTime}
              onChange={(date) => setEventDateTime(date)}
              showTimeSelect
              timeFormat={timeFormat}
              timeIntervals={15}
              dateFormat={dateFormat}
              locale={DATE_FNS_LOCALE[i18n.language] ?? enUS}
              placeholderText={t('createGathering.dateTimePlaceholder')}
              minDate={new Date()}
              className="datepicker-input"
              popperPlacement="bottom-start"
            />
          </div>

          <div className="form-section">
            <label>{t('createGathering.inviteFriends')}</label>
            <button
              className="select-friends-btn"
              onClick={() => setShowFriendDialog(true)}
            >
              {selectedFriends.length > 0
                ? t('createGathering.friendsSelected', { count: selectedFriends.length })
                : t('createGathering.selectFriends')}
            </button>
          </div>
        </div>

        <div className="dialog-footer">
          <button className="cancel-btn" onClick={onClose}>{t('common.cancel')}</button>
          <button
            className="create-btn"
            onClick={handleCreateGathering}
            disabled={creating || !eventDateTime}
          >
            {creating ? t('createGathering.creating') : t('createGathering.create')}
          </button>
        </div>

        <InviteFriendsDialog
          isOpen={showFriendDialog}
          onClose={() => setShowFriendDialog(false)}
          selectedFriends={selectedFriends}
          onToggle={handleFriendToggle}
          currentUser={currentUser}
        />
      </div>
    </div>
  );
}

export default OrganizeEventDialog;